import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import openai from '../../../services/openaiClient';
import { BarChart3, Sparkles, TrendingUp, FileText, Download, RefreshCw } from 'lucide-react';



const OverviewActionsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('insights');
  const [loading, setLoading] = useState(false);
  const [aiInsight, setAiInsight] = useState('');
  const [customQuery, setCustomQuery] = useState('');

  const generateAIInsight = async (type) => {
    setLoading(true);
    try {
      const prompts = {
        trends: 'قم بتحليل اتجاهات السوق العقاري الحالية وقدم رؤى استراتيجية للأشهر الثلاثة القادمة',
        performance: 'قم بتحليل أداء الأعمال الحالي وحدد نقاط القوة والضعف مع توصيات للتحسين',
        forecast: 'قدم توقعات للإيرادات والمبيعات للربع القادم بناءً على البيانات الحالية',
        custom: customQuery
      };

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: 'أنت محلل عقاري خبير متخصص في تحليل البيانات وتقديم رؤى استراتيجية للشركات العقارية.'
          },
          {
            role: 'user',
            content: prompts?.[type]
          }
        ],
        max_tokens: 500,
        temperature: 0.7
      });

      setAiInsight(response?.choices?.[0]?.message?.content);
    } catch (error) {
      console.error('Error generating AI insight:', error);
      setAiInsight('حدث خطأ في توليد التحليل. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'insights', label: 'رؤى ذكية', icon: Sparkles },
    { id: 'reports', label: 'التقارير', icon: FileText },
    { id: 'export', label: 'التصدير', icon: Download }
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="نظرة عامة - إجراءات وتحليلات" size="large" footer={null} icon={null} iconColor={null}>
      <div className="flex border-b border-border mb-6">
        {tabs?.map((tab) => {
          const TabIcon = tab?.icon;
          return (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <TabIcon className="w-4 h-4" />
              {tab?.label}
            </button>
          );
        })}
      </div>
      {activeTab === 'insights' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              onClick={() => generateAIInsight('trends')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <TrendingUp className="w-6 h-6" />
              <span>تحليل اتجاهات السوق</span>
            </Button>
            <Button
              onClick={() => generateAIInsight('performance')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <BarChart3 className="w-6 h-6" />
              <span>تحليل الأداء الشامل</span>
            </Button>
            <Button
              onClick={() => generateAIInsight('forecast')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <Sparkles className="w-6 h-6" />
              <span>توقعات ذكية</span>
            </Button>
            <div className="h-24 flex flex-col gap-2">
              <Input
                placeholder="استفسار مخصص..."
                value={customQuery}
                onChange={(e) => setCustomQuery(e?.target?.value)}
                className="flex-1"
              />
              <Button
                onClick={() => generateAIInsight('custom')}
                disabled={loading || !customQuery}
                size="sm"
              >
                <Sparkles className="w-4 h-4 ml-2" />
                تحليل
              </Button>
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-primary" />
              <span className="mr-3 text-muted-foreground">جاري التحليل...</span>
            </div>
          )}

          {aiInsight && !loading && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-foreground">تحليل ذكي</h3>
              </div>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{aiInsight}</p>
            </div>
          )}
        </div>
      )}
      {activeTab === 'reports' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'تقرير الأداء الشهري', desc: 'ملخص شامل للأداء خلال الشهر' },
              { title: 'تقرير المقارنة السنوية', desc: 'مقارنة الأداء مع العام السابق' },
              { title: 'تقرير التوقعات', desc: 'توقعات الأداء للربع القادم' },
              { title: 'تقرير مخصص', desc: 'إنشاء تقرير حسب معايير محددة' }
            ]?.map((report, idx) => (
              <div key={idx} className="border border-border rounded-lg p-4 hover:border-primary transition-colors cursor-pointer">
                <h4 className="font-medium text-foreground mb-1">{report?.title}</h4>
                <p className="text-xs text-muted-foreground mb-3">{report?.desc}</p>
                <Button size="sm" variant="outline">إنشاء التقرير</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab === 'export' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { format: 'PDF', desc: 'تصدير كملف PDF', color: 'text-red-500' },
              { format: 'Excel', desc: 'تصدير كملف Excel', color: 'text-green-500' },
              { format: 'CSV', desc: 'تصدير كملف CSV', color: 'text-blue-500' }
            ]?.map((option, idx) => (
              <Button key={idx} variant="outline" className="h-24 flex flex-col items-center justify-center gap-2">
                <Download className={`w-6 h-6 ${option?.color}`} />
                <div className="text-center">
                  <div className="font-medium">{option?.format}</div>
                  <div className="text-xs text-muted-foreground">{option?.desc}</div>
                </div>
              </Button>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default OverviewActionsModal;